import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginCredentials } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import { trackEvent } from '../utils/analytics';
import { buildBackendUrl } from '../services/api';
import { PENDING_SAVE_KEY } from '../constants/savedOpportunities';

const Login: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedReturnTo = searchParams.get('returnTo') ?? '';
  const returnTo = requestedReturnTo.startsWith('/') && !requestedReturnTo.startsWith('//') ? requestedReturnTo : '';
  const isCompletingSave = Boolean(window.localStorage.getItem(PENDING_SAVE_KEY));
  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: '',
    remember: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const u = await auth.login(formData);
      navigate(returnTo || (u?.is_premium ? '/home' : '/opportunities'));
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (auth.loading) {
    return <LoadingSpinner message="Loading..." />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          {isCompletingSave && (
            <p className="mt-3 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-center text-sm text-blue-800">
              Sign in or create an account to save this opportunity.
            </p>
          )}
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link
              to="/signup"
              state={returnTo ? { returnTo } : undefined}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              create a new account
            </Link>
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            trackEvent('login_start', { method: 'google' });
            const next = returnTo ? `?next=${encodeURIComponent(returnTo)}` : '';
            window.location.href = buildBackendUrl(`/auth/google${next}`);
          }}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
        >
          <GoogleIcon />
          Continue with Google
        </button>

        <div className="flex items-center gap-3 text-xs text-gray-400">
          <div className="flex-1 border-t border-gray-200" />
          <span>or continue with email</span>
          <div className="flex-1 border-t border-gray-200" />
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="input-field mt-1"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="input-field mt-1"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
              />
            </div>
            
            <div className="flex items-center">
              <input
                id="remember"
                name="remember"
                type="checkbox"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                checked={formData.remember}
                onChange={handleChange}
              />
              <label htmlFor="remember" className="ml-2 block text-sm text-gray-900">
                Remember me
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </button>
            <Link to="/forgot-password" className="ml-4 text-sm font-medium text-blue-600 hover:text-blue-500 whitespace-nowrap">
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;

const GoogleIcon: React.FC = () => (
  <svg role="img" aria-hidden className="h-5 w-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M23.49 12.27c0-.82-.07-1.64-.22-2.44H12v4.62h6.48a5.54 5.54 0 0 1-2.39 3.63v3h3.86c2.26-2.08 3.54-5.15 3.54-8.81z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.22 0 5.93-1.07 7.9-2.92l-3.86-3a7.43 7.43 0 0 1-4.04 1.16c-3.1 0-5.73-2.09-6.67-4.9H1.37v3.09A12 12 0 0 0 12 24z"
    />
    <path
      fill="#FBBC05"
      d="M5.33 14.34a7.2 7.2 0 0 1 0-4.68V6.57H1.37a12 12 0 0 0 0 10.86l3.96-3.09z"
    />
    <path
      fill="#EA4335"
      d="M12 4.75c1.75 0 3.32.6 4.55 1.78l3.41-3.41C17.93 1.13 15.22 0 12 0A12 12 0 0 0 1.37 6.57l3.96 3.09C5.71 7.84 8.34 4.75 12 4.75z"
    />
  </svg>
);
